import React from 'react';
import { TPropertiesOfPatchChangesForDish } from 'types';
import OutsideFormInput from 'components/Input/OutsideFormInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './Cron.module.scss';

interface IProps {
	changePrice(value: Nullable<number>): void;
	toggleHasOpenPrice(): void;
	changeOpenPrice(value: Nullable<number>): void;
	changeOpenPriceMaxThreshold(value: Nullable<number>): void;
	changeTaxPercent(value: Nullable<number>): void;
	toggleInStop(): void;
	toggleIsCommentVisible(): void;
	toggleIsFiscalized(): void;
	toggleIsTaxPercentProtectedFromSync(): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	selectedProperties: TPropertiesOfPatchChangesForDish[];
	price: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	taxPercent: Nullable<number>;
	inStop: boolean;
	isCommentVisible: boolean;
	isFiscalized: boolean;
	isTaxPercentProtectedFromSync: boolean;
	activationSchedule: string;
	deactivationSchedule: string;
	hasSchedulePriority: boolean;
}

export const Cron: React.FC<IProps> = ({
	changePrice,
	toggleHasOpenPrice,
	changeOpenPrice,
	changeOpenPriceMaxThreshold,
	changeTaxPercent,
	toggleInStop,
	toggleIsCommentVisible,
	toggleIsFiscalized,
	toggleIsTaxPercentProtectedFromSync,
	changeActivationSchedule,
	changeDeactivationSchedule,
	selectedProperties,
	price,
	hasOpenPrice,
	openPrice,
	openPriceMaxThreshold,
	taxPercent,
	inStop,
	isCommentVisible,
	isFiscalized,
	isTaxPercentProtectedFromSync,
	activationSchedule,
	deactivationSchedule,
	hasSchedulePriority,
}) => {
	return (
		<div className={style.container}>
			{selectedProperties.some((selectedProperty) =>
				['price', 'isDisabled', 'comments'].includes(selectedProperty),
			) && (
				<div className={style.row}>
					<div className={style.input}>
						{selectedProperties.includes('comments') && (
							<Checkbox onChange={toggleIsCommentVisible} checked={isCommentVisible}>
								<Text translationNameSpace="form" translationKey="commentsLabel" asFragment={true} />
							</Checkbox>
						)}
						{selectedProperties.includes('isDisabled') && (
							<Checkbox onChange={toggleInStop} checked={inStop}>
								<Text translationNameSpace="form" translationKey="inStopLabel" asFragment={true} />
							</Checkbox>
						)}
					</div>
					{selectedProperties.includes('price') && (
						<OutsideFormInput
							containerClassName={style.input}
							testId="PriceInput"
							name="price"
							label="signedPriceLabel"
							type="number"
							min={0}
							precision={0}
							numberValue={price}
							onChangeNumber={changePrice}
						/>
					)}
				</div>
			)}

			{selectedProperties.some((selectedProperty) => ['openPrice'].includes(selectedProperty)) && (
				<div className={style.row}>
					<div className={style.input}>
						{selectedProperties.includes('openPrice') && (
							<Checkbox onChange={toggleHasOpenPrice} checked={hasOpenPrice}>
								<Text
									translationNameSpace="form"
									translationKey="hasOpenPricePercentLabel"
									asFragment={true}
								/>
							</Checkbox>
						)}
					</div>
					{selectedProperties.includes('openPrice') && (
						<OutsideFormInput
							containerClassName={style.input}
							testId="OpenPriceInput"
							name="openPrice"
							label="openPricePercentLabel"
							type="number"
							min={0}
							precision={2}
							disabled={!hasOpenPrice}
							numberValue={openPrice}
							onChangeNumber={changeOpenPrice}
						/>
					)}
					{selectedProperties.includes('openPrice') && (
						<OutsideFormInput
							containerClassName={style.input}
							testId="OpenPriceMaxThresholdInput"
							name="openPriceMaxThreshold"
							label="openPriceMaxThresholdLabel"
							placeholder="openPriceMaxThresholdPlaceholder"
							type="number"
							min={0}
							precision={0}
							disabled={!hasOpenPrice}
							numberValue={openPriceMaxThreshold}
							onChangeNumber={changeOpenPriceMaxThreshold}
						/>
					)}
				</div>
			)}

			{selectedProperties.some((selectedProperty) =>
				['taxRate', 'isFiscalized', 'protectFromSync'].includes(selectedProperty),
			) && (
				<div className={style.row}>
					<div className={style.input}>
						{selectedProperties.includes('isFiscalized') && (
							<Checkbox onChange={toggleIsFiscalized} checked={isFiscalized}>
								<Text
									translationNameSpace="form"
									translationKey="isFiscalizedLabel"
									asFragment={true}
								/>
							</Checkbox>
						)}
						{selectedProperties.includes('protectFromSync') && (
							<Checkbox
								onChange={toggleIsTaxPercentProtectedFromSync}
								checked={isTaxPercentProtectedFromSync}>
								<Text
									translationNameSpace="form"
									translationKey="protectFromSyncLabel"
									asFragment={true}
								/>
							</Checkbox>
						)}
					</div>
					{selectedProperties.includes('taxRate') && (
						<OutsideFormInput
							containerClassName={style.input}
							testId="TaxPercentInput"
							name="taxPercent"
							label="taxRateLabel"
							type="number"
							min={0}
							max={100}
							precision={1}
							numberValue={taxPercent}
							onChangeNumber={changeTaxPercent}
						/>
					)}
				</div>
			)}

			{selectedProperties.some((selectedProperty) =>
				['activation', 'deactivation'].includes(selectedProperty),
			) && (
				<>
					<div className={style.row}>
						{selectedProperties.includes('activation') && (
							<OutsideFormInput
								containerClassName={style.input}
								testId="ActivationScheduleInput"
								name="activationSchedule"
								label="activationScheduleLabel"
								placeholder="cronExpressionInputPlaceholder"
								maxLength={255}
								value={activationSchedule}
								onChange={changeActivationSchedule}
							/>
						)}
						{selectedProperties.includes('deactivation') && (
							<OutsideFormInput
								containerClassName={style.input}
								testId="DeactivationScheduleInput"
								name="deactivationSchedule"
								label="deactivationScheduleLabel"
								placeholder="cronExpressionInputPlaceholder"
								maxLength={255}
								value={deactivationSchedule}
								onChange={changeDeactivationSchedule}
							/>
						)}
					</div>
					<CronGenerator />
				</>
			)}

			{selectedProperties.some((selectedProperty) => ['activation', 'deactivation'].includes(selectedProperty)) &&
				hasSchedulePriority && (
					<div className={style.infoWrapper}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfoTransparent`} />
						</svg>
						<Text
							translationNameSpace="form"
							translationKey="warningDishSchedule"
							theme="secondary"
							size="s"
							display="inline"
						/>
					</div>
				)}
		</div>
	);
};
